import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApolloError, ApolloQueryResult, useMutation, useQuery } from '@apollo/client';
import {
  PaywallRemoveCouponDoc,
  PaywallPreviewSubscriptionPurchaseDoc,
  PaywallSubscriptionPurchaseDoc,
  PaywallSubscriptionPurchaseMutation,
  PaywallSubscriptionPurchaseMutationVariables,
  BillingPlansQuery,
  TrialType,
} from '@minecraft.graphql-operations';
import { gqlBillingInfo } from '@minecraft.graphql-types';
import { captureExceptionEvent } from '@blocs.sentry';
import { useTranslation } from '@blocs.i18n';
import { QUERY_PARAMS, useUserContext } from '@blocs.auth';
import { publishPurchaseFlowEvent } from '@blocs.marketing-events';
import { HEAP_EVENT_NAMES, captureHeapEvent } from '@blocs.heap';
import { BrazeEvents, trackBrazeEvent } from '@blocs.braze';
import { MediaPurchase } from './enums/plans';
import { PaywallLocation, paywallLocationToBillingPath } from './enums/PaywallLocation';
import { parsePaymentsError } from './helpers/parseApolloError';
import { PlanType } from './usePlans';
import { UsePayment } from './types';
import { PaywallEvents } from './enums/events';
import { ThreeDActionType, ThreeDSecureErrorData, UseThreeDSecureFlow } from './ThreeDSecureFlow';

type UsePaymentProps = {
  selectedPlan: PlanType;
  selectedPaymentOption?: gqlBillingInfo;
  paywallLocation: PaywallLocation;
  isAnnualRenewalDue?: boolean;
  isAnnual?: boolean;
  redirectToOpenCall?: boolean;
  handleRefetchPlans?: () => Promise<ApolloQueryResult<BillingPlansQuery>>;
  setThreeDActionContext: UseThreeDSecureFlow['setThreeDActionContext'];
};

/**
 * Focuses on the actual payment API calls and responses
 * This also stores the current "purchase"
 */
export const usePayment = ({
  selectedPlan,
  selectedPaymentOption,
  paywallLocation,
  isAnnualRenewalDue,
  isAnnual,
  redirectToOpenCall,
  handleRefetchPlans,
  setThreeDActionContext,
}: UsePaymentProps): UsePayment => {
  const { t } = useTranslation();
  const [currentCouponCode, setCurrentCouponCode] = useState<string>();
  const [applyCouponError, setApplyCouponError] = useState<string>();
  const [completePurchaseError, setCompletePurchaseError] = useState<string>();
  const [completedPurchase, setCompletedPurchase] = useState<{
    data: PaywallSubscriptionPurchaseMutation['purchaseSubscription'];
    loading: boolean;
  }>({ data: null, loading: false });

  const { refreshTokens, subscription, accountId, previousSubscription } = useUserContext();

  const isAnnualRenewal = isAnnualRenewalDue && isAnnual && selectedPlan?.canRenew !== false;
  const selectedPlanBillingCode = selectedPlan?.billingCode;
  const skipPreviewPurchase =
    !!completedPurchase.data ||
    !selectedPlanBillingCode ||
    isAnnualRenewal ||
    selectedPlan?.billingCode === MediaPurchase.alacarte;

  const {
    data: previewPurchaseData,
    loading: previewPurchaseLoading,
    error: previewPurchaseError,
  } = useQuery(PaywallPreviewSubscriptionPurchaseDoc, {
    variables: {
      input: {
        subscriptionBillingCode: selectedPlanBillingCode,
        couponCode: currentCouponCode,
        billingPath: paywallLocationToBillingPath(paywallLocation, redirectToOpenCall),
      },
    },
    skip: skipPreviewPurchase,
    fetchPolicy: 'network-only',
  });

  const [removeCouponMutation, { loading: removeCouponLoading }] = useMutation(PaywallRemoveCouponDoc, {
    refetchQueries: [
      {
        query: PaywallPreviewSubscriptionPurchaseDoc,
        variables: {
          input: {
            subscriptionBillingCode: selectedPlanBillingCode,
            billingPath: paywallLocationToBillingPath(paywallLocation, redirectToOpenCall),
          },
        },
      },
    ],
    awaitRefetchQueries: true,
  });

  const [completePurchaseMutation, { loading: completePurchaseLoading }] = useMutation<
    PaywallSubscriptionPurchaseMutation,
    PaywallSubscriptionPurchaseMutationVariables
  >(PaywallSubscriptionPurchaseDoc, {
    update: (cache, { data }) => {
      if (!data?.purchaseSubscription) {
        return;
      }
      cache.evict({
        id: 'ROOT_QUERY',
        fieldName: 'searchArtistMedia',
      });

      cache.gc();
    },
  });

  useEffect(() => {
    setCompletePurchaseError(undefined);
    setApplyCouponError(undefined);
  }, [selectedPlanBillingCode]);

  useEffect(() => {
    // If a coupon application fails, it won't match the currentCouponCode in the `coupons` attribute
    if (!previewPurchaseError && !previewPurchaseLoading && previewPurchaseData && currentCouponCode) {
      const coupon =
        previewPurchaseData.previewSubscriptionPurchase.coupon?.code?.toLowerCase() ===
        currentCouponCode?.toLowerCase();

      if (!coupon) {
        setApplyCouponError(t('common:paywall.errors.INVALID_COUPON_CODE'));
      } else if (!previewPurchaseData.previewSubscriptionPurchase.coupon?.isApplicable) {
        setApplyCouponError(t('common:paywall.errors.INAPPLICABLE_COUPON_CODE'));
      } else {
        // If the coupon is valid, we need to remove any account level ones:
        removeCouponMutation()
          .then(() => undefined)
          .catch(() => undefined);
        setApplyCouponError(undefined);
      }
    } else if (previewPurchaseError) {
      const error = parsePaymentsError(previewPurchaseError);
      const errorString = t(`common:paywall.errors.${error}`);
      setApplyCouponError(errorString);
    } else {
      setApplyCouponError(undefined);
    }
  }, [previewPurchaseLoading, previewPurchaseData, currentCouponCode, removeCouponMutation, t, previewPurchaseError]);

  useEffect(() => {
    const preview = previewPurchaseData?.previewSubscriptionPurchase;

    if (preview) {
      publishPurchaseFlowEvent(PaywallEvents.beginCheckout, {
        location: paywallLocation,
        purchase: {
          subscriptions: [
            {
              name: `${preview.subscription.plan.name} - ${preview.subscription.plan.term.name}`,
              code: preview.subscription.plan.billingCode,
              price: preview.subscription.plan.price,
            },
          ],
          total: preview?.total,
          currency: preview?.currency,
          discount: preview?.creditsApplied,
        },
      });
    }
  }, [previewPurchaseData, paywallLocation]);

  const applyCoupon = useCallback(
    ({ couponCode }: { couponCode: string }) => {
      if (currentCouponCode !== couponCode) {
        setApplyCouponError(undefined);
      }

      // Setting this state variable will trigger the query to "test" the coupon code and then actually apply it
      setCurrentCouponCode(couponCode);
    },
    [currentCouponCode, setCurrentCouponCode]
  );

  const removeCoupon = useCallback(async () => {
    setApplyCouponError(undefined);
    try {
      // We may not have a coupon applied but we attempt to remove it either way
      // Coupons applied at the account level are removed via the removeCoupon mutation:
      await removeCouponMutation();
      // This triggers the previewPurchase again to set the currentCoupon
      setCurrentCouponCode(undefined);
    } catch (err) {
      // Any network errors are propagated via the useMutation hook
      captureExceptionEvent(err);
    }
  }, [removeCouponMutation]);

  const completePurchase = useCallback(
    async ({
      termsAccepted = false,
      billingInfoId,
      threeDSecureActionResultTokenId,
    }: {
      termsAccepted?: boolean;
      billingInfoId?: string;
      threeDSecureActionResultTokenId?: string;
    }) => {
      const coupon = previewPurchaseData?.previewSubscriptionPurchase?.coupon;
      try {
        /*
      If the coupon is NOT redeemed and IS applicable, we want to pass the code to the API
       */
        const couponCode = !coupon?.isRedeemed && coupon?.isApplicable ? coupon?.code : undefined;

        setCompletedPurchase({ loading: true, data: null });

        const paymentOptionId =
          selectedPaymentOption || billingInfoId ? String(selectedPaymentOption?.id ?? billingInfoId) : null;

        const response = await completePurchaseMutation({
          variables: {
            input: {
              subscriptionBillingCode: selectedPlanBillingCode,
              couponCode,
              trialTermsAccepted: termsAccepted,
              // The ID of the current payment method
              // If selectedPaymentOption is null, it is Free or it is 3DS flow;
              billingInfoId: !threeDSecureActionResultTokenId ? paymentOptionId : undefined,
              billingPath: paywallLocationToBillingPath(paywallLocation, redirectToOpenCall),
              subscriptionSiteLocation: paywallLocation,
              threeDSecureActionResultTokenId,
            },
          },
        });

        if (response.errors) {
          // There can be an error if we need to use the 3-D Secure flow
          const errorData = response.errors?.[0] as ThreeDSecureErrorData;
          const threeDToken: string =
            errorData?.data?.errorData?.threeDSecureActionTokenId ||
            errorData?.data?.errorData?.publicData?.threeDSecureActionTokenId;

          if (threeDToken) {
            setThreeDActionContext({ token: threeDToken, actionType: ThreeDActionType.PURCHASE });
            setCompletedPurchase({ loading: false, data: null });
          }
          throw new ApolloError({ graphQLErrors: response.errors });
        }

        const purchase = response.data?.purchaseSubscription;
        if (purchase) {
          const isPurchaseFromCardlessTrial =
            subscription?.trialType === TrialType.Cardless ||
            (previousSubscription?.trialType === TrialType.Cardless && subscription?.plan?.isBasePlan);

          if (isPurchaseFromCardlessTrial) {
            trackBrazeEvent(BrazeEvents.CardlessPurchase);
          }

          publishPurchaseFlowEvent(isPurchaseFromCardlessTrial ? 'purchase_success' : 'purchase', {
            location: paywallLocation,
            purchase: {
              subscriptions: [
                {
                  name: `${purchase.subscription.plan.name} - ${purchase.subscription.plan.term.name}`,
                  code: purchase.subscription.plan.billingCode,
                  price: purchase.subscription.plan.price,
                  trialEndsAt: purchase.subscription?.trialEndsAt ?? null,
                },
              ],
              total: purchase.total,
              couponCodes: [couponCode],
              currency: purchase.currency,
              creditsApplied: purchase.creditsApplied,
            },
            transactionId: purchase.invoiceId,
            currentPlan: subscription?.plan?.billingCode,
          });
          if (paywallLocation === PaywallLocation.SIGNUP) {
            captureHeapEvent({
              name: HEAP_EVENT_NAMES.SUCCESSFUL_TALENT_SIGN_UP,
              data: { accountId, membership: selectedPlanBillingCode },
            });
            trackBrazeEvent(BrazeEvents.ProfileCreationComplete);
          }

          await refreshTokens();
          await handleRefetchPlans();
          setThreeDActionContext(undefined);
          setCompletedPurchase({ loading: false, data: response.data?.purchaseSubscription });
          localStorage.removeItem(QUERY_PARAMS.planTerm as string);
        } else {
          setCompletedPurchase({
            loading: false,
            data: null,
          });
          setCompletePurchaseError(t('common:paywall.errors.TRANSACTION_ERROR'));
          throw new Error(t('common:paywall.errors.TRANSACTION_ERROR'));
        }
      } catch (err) {
        setCompletedPurchase({ loading: false, data: null });
        const error = parsePaymentsError(err);
        const errorString = t(`common:paywall.errors.${error}`) as string;
        setCompletePurchaseError(errorString);
        // Any network errors are propagated via the useMutation hook
        captureExceptionEvent(err);
        throw err;
      }
    },
    [
      previewPurchaseData,
      selectedPaymentOption,
      selectedPlanBillingCode,
      completePurchaseMutation,
      paywallLocation,
      redirectToOpenCall,
      setThreeDActionContext,
      subscription,
      previousSubscription,
      refreshTokens,
      handleRefetchPlans,
      accountId,
      t,
    ]
  );

  // The ongoing purchase the user is attempting to make
  const currentPurchase = useMemo(() => {
    if (previewPurchaseData?.previewSubscriptionPurchase) {
      return {
        name: previewPurchaseData.previewSubscriptionPurchase.subscription.plan.name,
        cardType: previewPurchaseData.previewSubscriptionPurchase.paymentDetails?.cardType,
        coupon: previewPurchaseData.previewSubscriptionPurchase.coupon?.isApplicable
          ? previewPurchaseData.previewSubscriptionPurchase.coupon
          : null,
        currency: previewPurchaseData.previewSubscriptionPurchase.currency,
        email: previewPurchaseData.previewSubscriptionPurchase.email,
        invoiceId: previewPurchaseData.previewSubscriptionPurchase.invoiceId,
        lastFour: previewPurchaseData.previewSubscriptionPurchase.paymentDetails?.lastFour,
        subscription: previewPurchaseData.previewSubscriptionPurchase.subscription,
        total: previewPurchaseData.previewSubscriptionPurchase.total,
        creditsApplied: previewPurchaseData.previewSubscriptionPurchase.creditsApplied,
        discount: previewPurchaseData.previewSubscriptionPurchase.discount ?? 0,
      };
    }

    return undefined;
  }, [previewPurchaseData?.previewSubscriptionPurchase]);

  return useMemo(
    () => ({
      loading: false,
      error: previewPurchaseError,
      currentPurchase,
      applyCoupon,
      applyCouponError,
      couponLoading: previewPurchaseLoading || removeCouponLoading,
      removeCoupon,
      completePurchase,
      completePurchaseError,
      completePurchaseLoading: completePurchaseLoading || completedPurchase?.loading,
      currencyCode: previewPurchaseData?.previewSubscriptionPurchase.currency.code,
      completedPurchase: completedPurchase?.data,
      setCompletedPurchase,
      setCompletePurchaseError,
    }),
    [
      previewPurchaseError,
      previewPurchaseLoading,
      removeCouponLoading,
      completePurchase,
      completePurchaseError,
      completePurchaseLoading,
      completedPurchase,
      currentPurchase,
      applyCoupon,
      applyCouponError,
      previewPurchaseData?.previewSubscriptionPurchase?.currency.code,
      removeCoupon,
    ]
  );
};
